<template>
    <div class="api-key-created-content">
        <div class="warning-banner">
            <i class="pi pi-exclamation-triangle"></i>
            Save this key now! You won't be able to see it again.
        </div>

        <div class="api-key-display">
            <span class="api-key-value">{{ apiKeyData.key }}</span>
            <Button
                icon="pi pi-copy"
                class="p-button-rounded p-button-text"
                @click="copyApiKey(apiKeyData.key || '')"
                :disabled="apiKeyCopied"
                :label="apiKeyCopied ? 'Copied!' : 'Copy'"
                aria-label="Copy API key to clipboard"
            />
        </div>

        <div class="api-key-info">
            <div class="info-row">
                <span class="info-label">Name:</span>
                {{ apiKeyData.name }}
            </div>
            <div class="info-row">
                <span class="info-label">Created:</span>
                {{ formatDate(apiKeyData.created_at) }}
            </div>
            <div class="info-row">
                <span class="info-label">Expires:</span>
                {{
                    apiKeyData.expires_at
                        ? formatDate(apiKeyData.expires_at)
                        : "Never"
                }}
            </div>
        </div>
    </div>
    <div class="buttons">
        <Button
            label="I have Saved My API Key"
            icon="pi pi-check"
            @click="dialogRef?.close"
            class="p-button-primary"
            autofocus
        />
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { Ref, inject, ref } from "vue";

/**
 * Generate MCP configuration JSON
 *
 * @param apiKey - The API key to use for authentication
 * @param organizationId - The organization ID to use in the configuration
 * @param stekId - The stek ID to use in the configuration
 * @param organizationName - The organization name to use in the server name
 * @param stekName - The stek name to use in the server name
 * @returns A JSON string containing the MCP configuration
 */
import type { ApiKeyCreated } from "@/lib/openapi/gen/types.gen";

interface DialogData {
    apiKey: ApiKeyCreated;
}

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const dialogData = dialogRef!.value.data as DialogData;
const apiKeyData = ref<ApiKeyCreated>(dialogData.apiKey);
const apiKeyCopied = ref(false);

const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    apiKeyCopied.value = true;
    window.setTimeout(() => {
        apiKeyCopied.value = false;
    }, 2000);
};

const formatDate = (dateStr?: Date | string | null): string => {
    if (!dateStr) return "Never";
    if (typeof dateStr === "string") {
        return new Date(dateStr).toLocaleString();
    } else {
        return dateStr.toLocaleString();
    }
};
</script>

<style scoped lang="scss">
.api-key-created-content {
    padding: 0.5rem 0 1rem;
}

.warning-banner {
    font-weight: 600;
    padding: 1rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    background-color: #fff3e0;
    color: #e65100;
    margin-bottom: 1rem;

    i {
        margin-right: 0.5rem;
    }
}

.api-key-display,
.config-display {
    background-color: #f5f5f5;
    padding: 1rem;
    border-radius: 4px;
    font-family: monospace;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #e0e0e0;
    margin: 1rem 0;
}

.api-key-value,
.config-value {
    word-break: break-all;
    font-size: 0.95rem;
    padding-right: 1rem;
    flex: 1;
    white-space: pre-wrap;
    overflow-x: auto;
}

.api-key-info {
    margin: 1rem 0;
}

.info-row {
    margin-bottom: 0.5rem;
    line-height: 1.5;
}

.info-label {
    font-weight: 600;
    color: #495057;
    margin-right: 0.5rem;
    display: inline-block;
    width: 100px;
}

.buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
}

h4 {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
}

.mt-4 {
    margin-top: 1.5rem !important;
}

.mb-2 {
    margin-bottom: 0.5rem !important;
}
</style>
